﻿import {
	Drivers,
	PermitId,
	Permits,
	PermitStatusComponent,
	Residencies,
	Tariffs,
	Vehicles,
	Zones
} from "@2d-front-monorepo/permits-business";

import {JSX, useEffect, useMemo, useRef} from "react";
import {useNavigate, useParams} from "react-router";
import InvalidPermitId from "../../components/InvalidPermitId";
import {PermitsBackButtonFrame} from "../../components/PermitsBackButton";
import Registrations from "../../customers/Registrations";
import getDriverHiddenFields from "../../hooks/getDriverHiddenFields";
import {getGlobalContext} from "../../hooks/getGlobalContext";
import getResidencyHiddenFields from "../../hooks/getResidencyHiddenFields";
import getVehicleHiddenFields from "../../hooks/getVehicleHiddenFields";
import {usePermits} from "../../hooks/usePermits";
import {PrivateRoutes} from "../../routes";
import {DriverWithChangeComponent} from "./ViewComponents/DriverWithChange";
import {FooterComponent} from "./ViewComponents/FooterComponent";
import {HolderComponent} from "./ViewComponents/HolderComponent";
import {ResidencyWithChangeComponent} from "./ViewComponents/ResidencyWithChange";
import {VehiclesComponent} from "./ViewComponents/VehiclesComponent";

export default function ViewPermit(): JSX.Element {
	const {permitId} = useParams<{ permitId: string }>() as { permitId: PermitId };
	const {installation} = getGlobalContext();
	const navigate = useNavigate();
	const permitsState = usePermits();
	// const {holderData} = permitsState;
	const permit = Permits.find(permitsState, permitId);
	// Only an id that WAS resolved and then vanished redirects - one that never resolved is a bad
	// link (or an archived permit, see Documents) and must keep saying so.
	const seen = useRef(false);
	seen.current ||= permit != null;

	useEffect(() => {
		// A multi-operation permit leaves the list the moment the backend splits it into its
		// `::group-name` children (SSE -> reloadPermits), and the wizard leaves the user right here
		// on it. What is now theirs are the children, and those are in the list.
		if (seen.current && permit == null) {
			navigate(PrivateRoutes.permitsList, {replace: true});
		}
	}, [permit, navigate]);

	const group = useMemo(() => {
		if (permit == null) return null;
		return {
			permits: [permit],
			permit: permit,
			name: "",
			count: 1,
			startDate: permit.startDate,
			endDate: permit.endDate
		};
	}, [permit]);

	if (permit == null) {
		// `seen` means the effect above is already navigating away: don't flash an error at them.
		return seen.current ? <></> : <InvalidPermitId permitId={permitId}/>;
	}

	const PermitComponent = Registrations.getPermitPage();

	const tariff = Tariffs.get(installation, permit.tariffId);
	const zone = Zones.get(installation, permit.parkingLocation.zoneId);

	const driver = Drivers.getChanged(permitsState, permit.driver);
	const residency = Residencies.getChanged(permitsState, permit.residency);
	const vehicles = Vehicles.getChanged(permitsState, permit.vehicles);

	const hiddenDriverFields = getDriverHiddenFields(installation, driver, permit.category);
	const hiddenResidencyFields = getResidencyHiddenFields(installation, residency, permit.category);
	const hiddenVehicleFields = getVehicleHiddenFields(installation, vehicles, permit.category);

	return (
		<>
			<div className="layout horizontal aligned middle">
				<h2 className="flex">{tariff.title}</h2>
				<PermitStatusComponent permit={permit}/>
			</div>
			<hr className="no-margin-start"/>
			<div className="layout vertical gap">
				<HolderComponent permit={permit}
				                 permits={permitsState}
				                 zone={residency == null ? zone : undefined}/>

				{residency && <ResidencyWithChangeComponent installation={installation}
                                                            residency={residency.original}
                                                            hiddenEntityProperties={hiddenResidencyFields}
                                                            changedResidency={residency.changed}/>}

				{driver && <DriverWithChangeComponent driver={driver.original}
                                                      hiddenEntityProperties={hiddenDriverFields}
                                                      changedDriver={driver.changed}/>}

				<VehiclesComponent vehicles={vehicles}
				                   hiddenEntityProperties={hiddenVehicleFields}/>

				<FooterComponent price={permit.payment?.operation?.step ?? permit.payment?.billing?.step} layout={permit.payment?.operation?.layout}/>
				<PermitComponent tariff={tariff}
				                 zone={zone}
				                 group={group!}
				                 permit={permit}
				                 permits={permitsState}
				                 driver={driver}
				                 residency={residency}
				                 vehicles={vehicles}
				                 now={installation.now}/>
			</div>

			<PermitsBackButtonFrame href={PrivateRoutes.permitsList}/>
		</>
	)
}
